import React, { useState } from "react";
import { RefreshCw, Copy, Bug, Home, Check } from "lucide-react";
import { SEO } from "../components/SEO";

interface SomethingWentWrongPageProps {
  /** Short human-readable message. Falls back to a generic string. */
  message?: string;
  /** Full error object — stack trace shown in collapsed detail pane. */
  error?: Error | null;
  /** Called on Retry. If omitted the button reloads the page. */
  onRetry?: () => void;
  /** Called on Back Home. If omitted navigates to window.location = "/". */
  onHome?: () => void;
}

/**
 * "Something Went Wrong" — triggered by backend errors, render errors, or
 * any unrecoverable API failure.
 *
 * Design rules (MasterPrompt):
 *  - Only design tokens, no hardcoded colors
 *  - Minimal enterprise aesthetic (Linear-inspired)
 *  - Actions: Retry · Copy Error · Report · Back Home
 */
export default function SomethingWentWrongPage({
  message,
  error,
  onRetry,
  onHome,
}: SomethingWentWrongPageProps) {
  const [copied, setCopied] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const displayMessage =
    message ||
    error?.message ||
    "An unexpected error occurred. The orchestration engine may be unavailable.";

  const errorText = error
    ? `${error.name}: ${error.message}\n\n${error.stack ?? ""}`
    : message ?? "Unknown error";

  const handleCopy = () => {
    navigator.clipboard.writeText(errorText).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReport = () => {
    const body = encodeURIComponent(
      `Error report from Orchy\n\n${errorText}`
    );
    window.open(
      `mailto:support@orchy.ai?subject=Error+Report&body=${body}`,
      "_blank"
    );
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleHome = () => {
    if (onHome) {
      onHome();
    } else {
      window.location.href = "/";
    }
  };

  return (
    <div className="min-h-screen bg-base flex flex-col items-center justify-center gap-8 p-8 text-center">
      <SEO title="Something Went Wrong" description="An error occurred in Orchy." noIndex />

      {/* Minimal SVG — disconnected DAG node, no cartoons */}
      <svg width="200" height="130" viewBox="0 0 200 130" fill="none" aria-hidden="true">
        {/* Grid */}
        <defs>
          <pattern id="sww-grid" width="16" height="16" patternUnits="userSpaceOnUse">
            <path d="M 16 0 L 0 0 0 16" fill="none" stroke="var(--af-border)" strokeWidth="0.4" />
          </pattern>
        </defs>
        <rect width="200" height="130" fill="url(#sww-grid)" opacity="0.5" />

        {/* Left node — healthy */}
        <rect x="16" y="48" width="52" height="34" rx="6"
          fill="var(--af-panel)" stroke="var(--af-border-strong)" strokeWidth="1.5" />
        <circle cx="29" cy="65" r="4" fill="var(--af-success)" />
        <rect x="38" y="60" width="22" height="5" rx="2.5" fill="var(--af-border)" />
        <rect x="38" y="68" width="14" height="4" rx="2" fill="var(--af-border)" />

        {/* Broken connector */}
        <path d="M 68 65 L 90 65" stroke="var(--af-border-strong)" strokeWidth="1.5"
          strokeDasharray="4 3" strokeLinecap="round" />
        {/* Break mark */}
        <path d="M 88 58 L 92 72" stroke="var(--af-error)" strokeWidth="2" strokeLinecap="round" />
        <path d="M 92 58 L 88 72" stroke="var(--af-error)" strokeWidth="2" strokeLinecap="round" />
        <circle cx="90" cy="65" r="8" stroke="var(--af-error)" strokeWidth="1" opacity="0.25">
          <animate attributeName="r" values="8;14;8" dur="2s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.25;0;0.25" dur="2s" repeatCount="indefinite" />
        </circle>

        {/* Right node — error state */}
        <rect x="110" y="48" width="74" height="34" rx="6"
          fill="var(--af-panel)" stroke="var(--af-error)" strokeWidth="1.5" strokeDasharray="5 2" />
        <circle cx="126" cy="65" r="4" fill="var(--af-error)">
          <animate attributeName="opacity" values="1;0.3;1" dur="1.6s" repeatCount="indefinite" />
        </circle>
        <rect x="136" y="60" width="36" height="5" rx="2.5" fill="var(--af-border)" />
        <rect x="136" y="68" width="22" height="4" rx="2" fill="var(--af-border)" />

        {/* Error label */}
        <text x="147" y="100" fill="var(--af-error)" fontSize="9" fontWeight="700"
          textAnchor="middle" fontFamily="monospace" opacity="0.8">
          EXEC_FAILURE
        </text>
      </svg>

      {/* Status badge */}
      <div className="flex flex-col items-center gap-3">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-[10px] font-bold font-mono tracking-widest"
          style={{ borderColor: "var(--af-error)", color: "var(--af-error)", background: "color-mix(in srgb, var(--af-error) 8%, transparent)" }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--af-error)" }} />
          EXECUTION_ERROR
        </span>

        <div className="max-w-sm space-y-2">
          <h1 className="text-xl font-bold text-text tracking-tight">
            Something went wrong
          </h1>
          <p className="text-sm text-secondary leading-relaxed">
            {displayMessage}
          </p>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={handleRetry}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all"
          style={{ background: "var(--af-primary)", color: "#fff" }}
        >
          <RefreshCw size={12} />
          Retry
        </button>

        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer border border-edge text-text hover:bg-card-hover transition-all"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied!" : "Copy Error"}
        </button>
      </div>
    </div>
  );
}
